"use client";

import { useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  WhatsAppBusinessProfile,
  WhatsAppMessageResult,
  MetaCatalogResult,
  MetaCatalogProduct,
  fetchWhatsAppBusinessProfile,
  sendWhatsAppReviewTemplate,
  fetchMetaCatalogProducts,
  createMetaReviewProduct,
} from "./metaReviewApi";

const DEFAULT_TEMPLATE_NAME =
  process.env.REACT_APP_META_REVIEW_TEMPLATE_NAME || "hello_world";

const DEFAULT_TEMPLATE_LANGUAGE =
  process.env.REACT_APP_META_REVIEW_TEMPLATE_LANGUAGE || "en_US";

const MetaReviewDemoPanel = () => {
  const [profile, setProfile] = useState<WhatsAppBusinessProfile | null>(null);
  const [messageResult, setMessageResult] =
    useState<WhatsAppMessageResult | null>(null);
  const [catalog, setCatalog] = useState<MetaCatalogResult | null>(null);
  const [createdProduct, setCreatedProduct] =
    useState<MetaCatalogProduct | null>(null);

  const [recipientPhoneNumber, setRecipientPhoneNumber] = useState("");
  const [templateName, setTemplateName] = useState(DEFAULT_TEMPLATE_NAME);
  const [languageCode, setLanguageCode] = useState(DEFAULT_TEMPLATE_LANGUAGE);

  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSendingTemplate, setIsSendingTemplate] = useState(false);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);

  const toast = useToast();

  const handleLoadProfile = async () => {
    try {
      setIsLoadingProfile(true);
      const result = await fetchWhatsAppBusinessProfile();
      setProfile(result);

      toast({
        title: "WhatsApp Business profile loaded",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Could not load WhatsApp profile",
        description: (error as Error).message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleSendTemplate = async () => {
    try {
      if (!recipientPhoneNumber.trim()) {
        throw new Error("Enter a reviewer WhatsApp number first.");
      }

      setIsSendingTemplate(true);

      const result = await sendWhatsAppReviewTemplate({
        recipientPhoneNumber,
        templateName,
        languageCode,
      });

      setMessageResult(result);

      toast({
        title: "WhatsApp review template sent",
        description: "Open WhatsApp to show the received message.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Could not send WhatsApp template",
        description: (error as Error).message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSendingTemplate(false);
    }
  };

  const handleLoadCatalog = async () => {
    try {
      setIsLoadingCatalog(true);
      const result = await fetchMetaCatalogProducts();
      setCatalog(result);

      toast({
        title: "Meta catalog products loaded",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Could not load catalog products",
        description: (error as Error).message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsLoadingCatalog(false);
    }
  };

  const handleCreateReviewProduct = async () => {
    try {
      setIsCreatingProduct(true);
      const result = await createMetaReviewProduct();
      setCreatedProduct(result);

      toast({
        title: "Review product created/synced",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Could not create review product",
        description: (error as Error).message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsCreatingProduct(false);
    }
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="rgba(15,23,42,0.10)"
      borderRadius="24px"
      p={{ base: 4, md: 6 }}
      bg="white"
      boxShadow="0 16px 40px rgba(15,23,42,0.08)"
      mb={6}
    >
      <VStack align="stretch" spacing={6}>
        <Stack spacing={2}>
          <Badge alignSelf="flex-start" colorScheme="purple">
            Meta Review Demo
          </Badge>

          <Heading size="md">PickDengu Meta Permission Test Panel</Heading>

          <Text fontSize="sm" color="gray.600">
            This panel is visible for Meta App Review. It demonstrates
            whatsapp_business_management, whatsapp_business_messaging, and
            catalog_management with clear captions.
          </Text>
        </Stack>

        <Alert status="info" borderRadius="16px">
          <AlertIcon />
          <Text fontSize="sm">
            Screencast caption: Start here after Meta Login. Each section below
            demonstrates one requested Meta permission.
          </Text>
        </Alert>

        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={5}>
          <Box
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="20px"
            p={4}
          >
            <Stack spacing={4}>
              <Stack spacing={1}>
                <Badge colorScheme="blue" alignSelf="flex-start">
                  whatsapp_business_management
                </Badge>
                <Heading size="sm">WhatsApp Business Account</Heading>
                <Text fontSize="sm" color="gray.600">
                  Loads the connected WhatsApp Business Account and phone number
                  profile details.
                </Text>
              </Stack>

              <Tooltip label="Calls the backend to read the connected WhatsApp Business Account and phone number profile.">
                <Button
                  colorScheme="blue"
                  onClick={handleLoadProfile}
                  isLoading={isLoadingProfile}
                  loadingText="Loading"
                >
                  Load WhatsApp Business Profile
                </Button>
              </Tooltip>

              {profile && (
                <Stack spacing={2} fontSize="sm">
                  <Text>
                    <strong>Business:</strong> {profile.businessName}
                  </Text>
                  <Text>
                    <strong>WABA ID:</strong> {profile.wabaId}
                  </Text>
                  <Text>
                    <strong>Phone Number ID:</strong> {profile.phoneNumberId}
                  </Text>
                  <Text>
                    <strong>Display Number:</strong>{" "}
                    {profile.displayPhoneNumber}
                  </Text>
                  <Text>
                    <strong>Verified Name:</strong> {profile.verifiedName}
                  </Text>
                  <Text>
                    <strong>Status:</strong> {profile.status}
                  </Text>
                  <Text>
                    <strong>Quality:</strong> {profile.qualityRating}
                  </Text>
                </Stack>
              )}
            </Stack>
          </Box>

          <Box
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="20px"
            p={4}
          >
            <Stack spacing={4}>
              <Stack spacing={1}>
                <Badge colorScheme="green" alignSelf="flex-start">
                  whatsapp_business_messaging
                </Badge>
                <Heading size="sm">Send WhatsApp Template</Heading>
                <Text fontSize="sm" color="gray.600">
                  Sends a test template/message and displays the returned
                  WhatsApp message ID.
                </Text>
              </Stack>

              <FormControl>
                <FormLabel fontSize="sm">Recipient WhatsApp number</FormLabel>
                <Input
                  placeholder="Example: 265999000000"
                  value={recipientPhoneNumber}
                  onChange={(e) =>
                    setRecipientPhoneNumber(
                      e.target.value.replace(/[^\d+]/g, ""),
                    )
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Template name</FormLabel>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Language code</FormLabel>
                <Input
                  value={languageCode}
                  onChange={(e) => setLanguageCode(e.target.value)}
                />
              </FormControl>

              <Tooltip label="Sends an approved WhatsApp template from PickDengu to the reviewer test number.">
                <Button
                  colorScheme="green"
                  onClick={handleSendTemplate}
                  isLoading={isSendingTemplate}
                  loadingText="Sending"
                >
                  Send Review WhatsApp Template
                </Button>
              </Tooltip>

              {messageResult && (
                <Stack spacing={2} fontSize="sm">
                  <Text>
                    <strong>Status:</strong> {messageResult.status}
                  </Text>
                  <Text>
                    <strong>Message ID:</strong> {messageResult.messageId}
                  </Text>
                  <Text>
                    <strong>Recipient:</strong> {messageResult.recipient}
                  </Text>
                  <Text>
                    <strong>Template:</strong> {messageResult.templateName}
                  </Text>
                </Stack>
              )}
            </Stack>
          </Box>

          <Box
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="20px"
            p={4}
          >
            <Stack spacing={4}>
              <Stack spacing={1}>
                <Badge colorScheme="orange" alignSelf="flex-start">
                  catalog_management
                </Badge>
                <Heading size="sm">Meta Catalog Products</Heading>
                <Text fontSize="sm" color="gray.600">
                  Displays and creates/syncs products for the connected Meta
                  catalog.
                </Text>
              </Stack>

              <Tooltip label="Loads products from the connected Meta catalog.">
                <Button
                  colorScheme="orange"
                  onClick={handleLoadCatalog}
                  isLoading={isLoadingCatalog}
                  loadingText="Loading"
                >
                  Load Meta Catalog Products
                </Button>
              </Tooltip>

              <Tooltip label="Creates or syncs a safe test product for App Review demonstration.">
                <Button
                  variant="outline"
                  onClick={handleCreateReviewProduct}
                  isLoading={isCreatingProduct}
                  loadingText="Creating"
                >
                  Create Review Product
                </Button>
              </Tooltip>

              {catalog && (
                <Stack spacing={2} fontSize="sm">
                  <Text>
                    <strong>Catalog:</strong> {catalog.catalogName}
                  </Text>
                  <Text>
                    <strong>Catalog ID:</strong> {catalog.catalogId}
                  </Text>

                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Product</Th>
                        <Th>Price</Th>
                        <Th>Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {catalog.products.map((product) => (
                        <Tr key={product.id}>
                          <Td>{product.name}</Td>
                          <Td>{product.price}</Td>
                          <Td>{product.availability}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Stack>
              )}

              {createdProduct && (
                <Alert status="success" borderRadius="12px">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Created/synced: {createdProduct.name}
                  </Text>
                </Alert>
              )}
            </Stack>
          </Box>
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

export default MetaReviewDemoPanel;
